// Client for the Go data-pipeline portal (services/data-pipeline), a distinct
// Go service from the Python api. It reuses the platform login token, which the
// portal validates against auth.session (same tenant, same DB). All calls send
// Authorization: Bearer <platform JWT>.
import { PIPELINE_API_URL } from '../config';
import { authService } from './authService';

function authHeaders(extra = {}) {
  const token = authService.requireToken();
  return { Authorization: `Bearer ${token}`, ...extra };
}

async function getJSON(path) {
  const res = await fetch(`${PIPELINE_API_URL}${path}`, { headers: authHeaders() });
  if (res.status === 401) {
    authService.handleSessionExpired();
    throw new Error('Session expired — please sign in again.');
  }
  if (res.status === 403) throw new Error('You do not have permission for the pipeline.');
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new Error((payload && payload.error) || `Request failed (${res.status})`);
  return payload;
}

// postForm sends an x-www-form-urlencoded trigger and asks for a JSON reply
// ({ run_id }); the portal falls back to htmx HTML for non-JSON clients.
async function postForm(path, fields = {}) {
  const body = new URLSearchParams();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') body.set(k, String(v));
  });
  const res = await fetch(`${PIPELINE_API_URL}${path}`, {
    method: 'POST',
    headers: authHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json'
    }),
    body: body.toString()
  });
  if (res.status === 401) {
    authService.handleSessionExpired();
    throw new Error('Session expired — please sign in again.');
  }
  if (res.status === 403) throw new Error('Only admins can start pipeline runs.');
  if (res.status === 409) throw new Error('A run is already in progress — wait for it to finish.');
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new Error((payload && payload.error) || `Could not start run (${res.status})`);
  if (!payload || !payload.run_id) throw new Error('Run started but no run id was returned.');
  return payload.run_id;
}

// postAction sends a bodyless POST (pause/cancel) and returns the JSON reply. 409
// means there is no in-flight job to act on (it may have just finished).
async function postAction(path) {
  const res = await fetch(`${PIPELINE_API_URL}${path}`, {
    method: 'POST',
    headers: authHeaders({ Accept: 'application/json' })
  });
  if (res.status === 401) {
    authService.handleSessionExpired();
    throw new Error('Session expired — please sign in again.');
  }
  if (res.status === 403) throw new Error('Only admins can control pipeline runs.');
  if (res.status === 409) throw new Error('No active job to act on — it may have finished.');
  const payload = await res.json().catch(() => null);
  if (!res.ok) throw new Error((payload && payload.error) || `Request failed (${res.status})`);
  return payload;
}

export const pipelineService = {
  // The 14-field completion contract with live fill/coverage state.
  getCoverage() {
    return getJSON('/inspect/coverage');
  },

  // Recent ingest runs (lineage.run, pipeline='ingest').
  listRuns() {
    return getJSON('/runs');
  },

  getRun(runId) {
    return getJSON(`/runs/${runId}`);
  },

  // Start the enrichment recipe (the 7-step ratchet). provider:
  // deterministic | openai | ollama. Caps bound external spend (0 = default).
  // steps: array of step names to run (empty/omitted = all). Returns the job id.
  startEnrichment({ provider = 'deterministic', companyCap = '', peopleCap = '', country = '', steps } = {}) {
    return postForm('/runs/enrichment', {
      provider,
      company_cap: companyCap,
      people_cap: peopleCap,
      country,
      steps: Array.isArray(steps) && steps.length ? steps.join(',') : ''
    });
  },

  // Pause / cancel an in-flight enrichment job (graceful — takes effect at the
  // next chunk boundary). Returns { status: 'pausing' | 'cancelling' }.
  pauseJob(jobId) {
    return postAction(`/runs/${jobId}/pause`);
  },
  cancelJob(jobId) {
    return postAction(`/runs/${jobId}/cancel`);
  },

  // Resume a paused enrichment job. Returns the job id (the run re-attaches to the
  // same SSE channel). 409 if another run is active or the job is not paused.
  resumeJob(jobId) {
    return postForm(`/runs/${jobId}/resume`);
  },

  // Durable job status + per-step progress (rows_in/rows_out) — survives a reload.
  getJob(jobId) {
    return getJSON(`/runs/${jobId}/job`);
  },

  // The tenant's current in-flight enrichment job (running or paused), so the UI
  // can rediscover a run on page load without client-side state. { job_id: null }
  // when there is none.
  getActiveJob() {
    return getJSON('/runs/enrichment/active');
  },

  // Re-run a single field enricher over its backlog.
  startEnricher({ enricher, provider = 'deterministic', companyLimit = '' } = {}) {
    return postForm('/runs/enrich', { enricher, provider, company_limit: companyLimit });
  },

  // Re-ingest from the raw source (deterministic; no external calls).
  startIngest({ mode = 'full', sampleSize = '' } = {}) {
    return postForm('/runs/ingest', { mode, sample_size: sampleSize });
  },

  // EventSource cannot set headers, so the token rides as a query param the
  // portal accepts for the stream endpoint only.
  streamUrl(runId) {
    const token = authService.getToken();
    return `${PIPELINE_API_URL}/runs/${runId}/stream?access_token=${encodeURIComponent(token)}`;
  }
};
